import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  type ErrorComponentProps,
  Navigate,
  Outlet,
  useMatchRoute,
} from "@tanstack/react-router";
import clsx from "clsx";
import { useMemo } from "react";

import { BookingError } from "@/components/Errors/BookingError";
import { OfferNotAvailableError } from "@/components/Errors/OfferNotAvailableError";
import { WhatsAppButton } from "@/components/WhatsApp/WhatsAppButton";
import { usePurchaseStateContext } from "@/contexts/PurchaseStateContext";
import { HotelBookingContextProvider } from "@/features/hotels/contexts/HotelBookingContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { HotelTravellersSelectionProvider } from "@/features/hotels/contexts/HotelTravellersSelectionContext";
import { buildHotelSearchCriteria } from "@/features/hotels/utils/buildHotelSearchCriteria";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import { getGroupedTravellersFromRooms } from "@/features/travellers/utils/transformTraveler";
import { useRouteConfig } from "@/hooks/useRouteConfig";
import { CommonTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import { type OfferSelectedEventData } from "@/types/types";
import { isOfferIsNotAvailableAnymoreError } from "@/utils/assertions";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer",
)({
  component: RouteComponent,
  errorComponent: (props: ErrorComponentProps) => {
    Sentry.captureException(props.error);

    if (isOfferIsNotAvailableAnymoreError(props.error)) {
      return <OfferNotAvailableError variant="hotel" />;
    }

    return <BookingError {...props} type="hotels" />;
  },
  onEnter: () => {
    const event = new CustomEvent<OfferSelectedEventData>("offerselected", {
      detail: {
        offerType: "hotel",
      },
    });
    window.dispatchEvent(event);
  },
});

function RouteComponent() {
  const matches = useMatchRoute();
  const { purchaseState } = usePurchaseStateContext();
  const { searchData } = useHotelSearchDataContext();
  useQuery(travellersQueryOptions()); // for preloading

  const routeConfig = useRouteConfig();

  const isDetailsRoute = matches({
    from: "/",
    to: "/hotels/$id/details",
  });

  const isLastStepRoute = matches({
    from: "/",
    to: "/hotels/$id/confirmation",
  });

  const isGalleryRoute =
    !!matches({ from: "/", to: "/hotels/$id/details/images" }) ||
    !!matches({ from: "/", to: "/hotels/$id/details/images/fullscreen" });

  const initialTravellersSlots = useMemo(() => {
    if (!searchData) return;

    const travellersToSelectByAge = getGroupedTravellersFromRooms(
      searchData.rooms,
    );

    return {
      ADT: Array.from({ length: travellersToSelectByAge.adults }, () => null),
      CHD: Array.from({ length: travellersToSelectByAge.children }, () => null),
      INF: Array.from({ length: travellersToSelectByAge.infants }, () => null),
    };
  }, [searchData]);

  if (!isLastStepRoute && purchaseState === "completed") {
    return <Navigate to="/" />;
  }

  return (
    <main
      className={clsx("no-footer main h-screen", {
        "with-drawer": !!routeConfig.drawer,
      })}
    >
      <div className={isDetailsRoute ? "pt-2" : "pt-4"}>
        <HotelBookingContextProvider>
          <HotelTravellersSelectionProvider
            key={`${initialTravellersSlots?.ADT.length}-${initialTravellersSlots?.CHD.length}-${initialTravellersSlots?.INF.length}`}
            initialTravellersSlots={initialTravellersSlots}
            travelDate={searchData!.dates.checkIn}
          >
            <Outlet />
          </HotelTravellersSelectionProvider>
        </HotelBookingContextProvider>
      </div>

      {!isLastStepRoute && !isGalleryRoute && (
        <WhatsAppButton
          bottomPosition="20vh"
          onClick={() => {
            rudderAnalytics.track(
              CommonTrackingEvents.USER_CLICKED_WHATSAPP_BUTTON,
              {
                search_criteria: searchData
                  ? buildHotelSearchCriteria(searchData)
                  : undefined,
              },
            );
          }}
        />
      )}
    </main>
  );
}
